import { z } from 'zod';
/**
 * What is happening on the job, while it is happening.
 * [Teen steps 15/17, Parent steps 14-16, Job Poster steps 23-24]
 *
 * Two separate things, deliberately kept apart. Progress is the teen narrating
 * the work: milestones, blockers, done. Location is far more sensitive, so it
 * is opt-in, per assignment, stoppable at any moment, and it ends when the work
 * does — a marketplace has no business knowing where a teen is on a Tuesday
 * when they are not working.
 */
/* -------------------------------------------------------------------------- */
/* Progress                                                                   */
/* -------------------------------------------------------------------------- */
export const progressStatusSchema = z.enum(['IN_PROGRESS', 'BLOCKED', 'COMPLETED']);
export const progressUpdateSchema = z.object({
    id: z.string(),
    assignmentId: z.string(),
    milestone: z.string(),
    status: progressStatusSchema,
    notes: z.string().nullable(),
    proofUrl: z.string().nullable(),
    updatedAt: z.string(),
});
export const createProgressUpdateInputSchema = z.object({
    milestone: z.string().trim().min(1, 'Say what you did').max(150),
    status: progressStatusSchema.default('IN_PROGRESS'),
    notes: z.string().trim().max(2000).optional(),
    /** A photo of the finished work, once uploads exist. */
    proofUrl: z.string().url().max(255).optional(),
});
/* -------------------------------------------------------------------------- */
/* Location                                                                   */
/* -------------------------------------------------------------------------- */
export const trackingStatusSchema = z.enum(['EN_ROUTE', 'ARRIVED', 'DEPARTED']);
export const liveLocationStatusSchema = z.enum(['EN_ROUTE', 'ARRIVED', 'DEPARTED', 'IDLE']);
/** A coordinate, bounded to real values so a typo cannot land in the Atlantic. */
export const latitudeSchema = z.number().min(-90).max(90);
export const longitudeSchema = z.number().min(-180).max(180);
export const shareLocationInputSchema = z.object({
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    /** GPS accuracy radius in metres, when the device reports one. */
    accuracyM: z.number().int().min(0).max(100_000).optional(),
    status: trackingStatusSchema,
});
export const locationPingSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
    status: trackingStatusSchema,
    recordedAt: z.string(),
});
/**
 * Where the teen is, if they are sharing.
 *
 * `sharing: false` with a null position is the normal, expected state — not an
 * error, and not something a screen should present as a fault.
 */
export const liveLocationSchema = z.object({
    assignmentId: z.string(),
    sharing: z.boolean(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    accuracyM: z.number().int().nullable(),
    status: liveLocationStatusSchema,
    updatedAt: z.string().nullable(),
    /** The recent trail, newest first. Empty when nothing has been shared. */
    trail: z.array(locationPingSchema),
});
//# sourceMappingURL=worksite.js.map